/* eslint-disable no-shadow */
import moment from 'moment-timezone';
import errorHandler from '@/utils/error.handler';

// eslint-disable-next-line import/prefer-default-export
export const getters = {
  activeConversation(state) {
    return state.conversations.find(({ client }) => client.id === state.activeClientId);
  },
  activeConversationHistory(state) {
    if (state.activeClientHistoryId) {
      return state.conversationsHistory.find(
        ({ conversation }) => conversation.id === state.activeClientHistoryId,
      );
    }

    return null;
  },
  chatIsLoading(state) {
    return (clientId) => {
      if (state.conversationStates[clientId]) {
        return state.conversationStates[clientId].loading ?? false;
      }

      return false;
    };
  },
  chatFirstLoad(state) {
    return (clientId) => {
      if (state.conversationStates[clientId]) {
        return state.conversationStates[clientId].firstLoad ?? true;
      }

      return true;
    };
  },
  chatTopReached(state) {
    return (clientId) => {
      if (state.conversationStates[clientId]) {
        return state.conversationStates[clientId].topReached ?? false;
      }

      return false;
    };
  },
  chatCurrentInput(state) {
    return (clientId) => {
      if (state.conversationStates[clientId]) {
        return state.conversationStates[clientId].currentInput ?? '';
      }

      return '';
    };
  },
  chatOldestMessageId(state) {
    return (clientId) => {
      const indexConversation = state.conversations.findIndex(
        ({ client }) => client.id === clientId,
      );

      if (indexConversation !== -1) {
        if (state.conversations[indexConversation].messages.length > 0) {
          const oldestMessage = state.conversations[indexConversation].messages[0];

          return oldestMessage.id;
        }
        errorHandler.logErrors(`conversation has no messages ${clientId}`);
      } else {
        console.warn(`conversation not found ${clientId}`);
      }

      return null;
    };
  },
  chatHistoryOldestMessageId(state) {
    return (clientId) => {
      const indexConversation = state.conversationsHistory.findIndex(
        ({ conversation }) => conversation.id === clientId,
      );

      if (indexConversation !== -1) {
        if (state.conversationsHistory[indexConversation].messages.length > 0) {
          const oldestMessage = state.conversationsHistory[indexConversation].messages[0];

          return oldestMessage.id;
        }
        errorHandler.logErrors(`conversation has no messages ${clientId}`);
      } else {
        console.warn(`conversation not found ${clientId}`);
      }

      return null;
    };
  },
  userInfo(state) {
    return {
      id: state.user.id,
      name: state.user.name,
      profilePic: state.user.profilePic,
      status: state.user.status,
    };
  },
  configuration(state) {
    return state.configuration.configuration;
  },
  getUserCampaigns(state, getters) {
    return getters.configuration.campaigns;
  },
  getConfig(state, getters) {
    return (campaignId, channelId) => {
      const campaign = getters.configuration.campaigns.find(
        (campaign) => campaign.id === campaignId,
      );
      const channel = getters.configuration.channels.find(
        (channel) => channel.channelId === channelId,
      );
      const campaignCfg = campaign ? campaign.config : {};
      const channelCfg = channel ? channel.config : {};

      return channel?.overrideConfig ? channelCfg : campaignCfg;
    };
  },
  getAllCategories(state, getters) {
    return (campaignId) => {
      const campaign = getters.configuration.campaigns.find(
        (campaign) => campaign.id === campaignId,
      );

      if (campaign) {
        return campaign.categories;
      }

      return [];
    };
  },
  dataBank(state) {
    return state.configuration.dataBank;
  },
  templates(state) {
    return state.configuration.templates;
  },
  dataBankText: (state, getters) => ({ campaignId }) => getters.dataBank.filter(
    ({
      campaign,
      type,
    }) => campaign === campaignId && type === 'text',
  ),
  dataBankImages: (state, getters) => ({ campaignId }) => getters.dataBank.filter(
    ({
      campaign,
      type,
    }) => campaign === campaignId && type === 'image',
  ),
  dataBankDocuments: (state, getters) => ({ campaignId }) => getters.dataBank.filter(
    ({
      campaign,
      type,
    }) => campaign === campaignId && type === 'document',
  ),
  dataBankVideos: (state, getters) => ({ campaignId }) => getters.dataBank.filter(
    ({
      campaign,
      type,
    }) => campaign === campaignId && type === 'video',
  ),
  dataBankLocations: (state, getters) => ({ campaignId }) => getters.dataBank.filter(
    ({
      campaign,
      type,
    }) => campaign === campaignId && type === 'location',
  ),
  templateText: (state, getters) => ({ campaignId }) => getters.templates.filter(
    ({
      campaign,
      settings,
    }) => campaign.id === campaignId && settings.message_type === 'text',
  ),
  labelsByCampaignId: (state, getters) => (campaignId) => {
    if (getters.configuration
      && getters.configuration.campaigns
      && getters.configuration.campaigns.length > 0) {
      return getters.configuration.campaigns
        .filter(({ id }) => id === campaignId)
        .map((campaign) => campaign.labels);
    }
    return [];
  },
  totalUnreadConversations(state) {
    let totalConversationsUnread = 0;

    state.conversations.forEach((el) => {
      const hasUnreadMessages = el.messages.some((m) => m.typeUser === 'Client' && m.readAt === null);

      if (hasUnreadMessages) {
        totalConversationsUnread += 1;
      }
    });

    return totalConversationsUnread;
  },
  totalUnreadMessages(state) {
    let totalUnread = 0;

    state.conversations.forEach((el) => {
      const unread = el.messages.filter((m) => m.typeUser === 'Client' && m.readAt === null);

      totalUnread += unread.length;
    });

    return totalUnread;
  },
  hasUnreadMessages(state, getters) {
    return getters.totalUnreadMessages > 0;
  },
  getUserMultimedia(state) {
    return (id, type) => {
      if (state.userMultimedia[id] && state.userMultimedia[id][type]) {
        const multimedia = state.userMultimedia[id][type].multimedia
          .reduce((groups, msg) => {
            const date = moment(msg.createdAt).format('MMMM YYYY');
            const groupDate = groups[date] || [];
            groupDate.push(msg);
            return { ...groups, [date]: groupDate };
          }, {});

        return { ...state.userMultimedia[id][type], multimedia };
      }
      return null;
    };
  },
  multimediaLastId(state) {
    return (id, type) => {
      if (state.userMultimedia[id] && state.userMultimedia[id][type]) {
        const { multimedia } = state.userMultimedia[id][type];
        return multimedia[multimedia.length - 1].id;
      }
      return null;
    };
  },
  hasMultimedia(state) {
    return (id, type) => !!state.userMultimedia[id] && !!state.userMultimedia[id][type];
  },
  getLanguage(store) {
    return store.language;
  },
  disabledWhatsapp(state, getters) {
    if (getters.activeConversationHistory
      && getters.activeConversationHistory.conversation
      && getters.activeConversationHistory.conversation.channel
      && getters.activeConversationHistory.conversation.channel.channelType
      && getters.activeConversationHistory.conversation.channel.channelType.key === 'whatsapp') {
      if (getters.activeConversationHistory.conversation.lastMessageAt) {
        const { lastMessageAt } = getters.activeConversationHistory.conversation;
        const lastMessage = moment.utc(lastMessageAt).local();
        const diff = moment().diff(lastMessage, 'minutes');
        if (diff >= 1439) {
          return true;
        }
      }
      return true;
    }
    return false;
  },
  theme(state) {
    return state.theme;
  },
  failedJobMessage(state) {
    return (id) => state.failedMessages.find((el) => el.id === id && el.status === 'failed');
  },
  messageFromHistory(state) {
    return state.messageFromHistory;
  },

};
